import React from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Typography,
  CardContent,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import { getYesNoArray } from "@icarius-utils/constants";
import useHandleForm from "./useHandleForm";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import ReadOnlyInput from "@icarius-common/dynamicInputs/readOnlyInput";

const gridStyle = { padding: 10 };

const MenuFormDialog = (props) => {

  const {
    open,
    data,
    submitCallback,
    handleClose,
    isLoading,
  } = props;

  const dispatch = useDispatch();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, submitCallback, openValidationError);


  if (!data) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {`${getLocalizedString("updateMenu")}: ${formData.name}`}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <Loader open={isLoading} />
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id={`label-editable`}>{getLocalizedString("editable")}</InputLabel>
                <Select
                  disabled
                  value={data.editable ? "Y" : "N"}
                  labelId={`label-editable`}
                  id={`select-editable`}
                  margin={"none"}
                >
                  {
                    getYesNoArray().map((item) => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id={`label-active`}>{getLocalizedString("active")}</InputLabel>
                <Select
                  disabled={!data?.editable}
                  value={formData.active ? "Y" : "N"}
                  labelId={`label-active`}
                  id={`select-active`}
                  onChange={(e) => setFormValue(e.target.value === "Y", "active")}
                  margin={"none"}
                >
                  {
                    getYesNoArray().map((item) => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextFieldMultiline
                disabled={!data?.editable}
                required
                fullWidth
                rows={3}
                margin={"none"}
                label={getLocalizedString("description")}
                value={formData.description}
                onChange={(e) => setFormValue(e.target.value.substring(0, 250), "description")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextFieldMultiline
                disabled={!data?.editable}
                fullWidth
                rows={6}
                margin={"none"}
                label={getLocalizedString("visualizationFilter")}
                value={formData.sqlFilter}
                onChange={(e) => setFormValue(e.target.value, "sqlFilter")}
                helperText={<span style={{ whiteSpace: "pre-wrap" }}>{getLocalizedString("menuVisualizationFilterHelperText")}</span>}
              />
            </Grid>
          </Grid>
          <Grid container item alignItems="center" justify="center" xs={12} style={{ ...gridStyle, paddingTop: 30 }}>
            <Typography className="whiteText" variant="h6">
              {getLocalizedString("menuData")}
            </Typography>
          </Grid>
          <Card style={{ width: "100%", background: "var(--tertiaryBackgroundColor)" }}>
            <CardContent>
              <Grid container item xs={12}>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("path")}
                    value={`/${data?.path}`}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("module")}
                    value={data?.moduleString}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("role")}
                    value={data?.roleString}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("type")}
                    value={data?.typeString}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("childrenOf")}
                    value={data?.childOfString}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("hiddenByChildren")}
                    value={data?.hiddenByChildrenString}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                  <ReadOnlyInput
                    label={getLocalizedString("requiredMenu")}
                    value={data?.requiredMenuString}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default MenuFormDialog;
